import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Film, Plus, Calendar as CalendarIcon, AlertCircle, ExternalLink, CalendarClock, XCircle, List, LayoutGrid } from 'lucide-react';
import { api } from '../../lib/api';
import CalendarView from '../../components/media/MediaCalendar/MediaCalendar';
import MediaEventDetail from '../../components/media/MediaCalendar/MediaEventDetail';
import {
  packageStatusBadgeClass,
  packageStatusLabel,
  eventStatusBadgeClass,
  eventStatusLabel,
  noticeTierBadgeClass,
  noticeTierLabel,
  formatMediaDateRange,
  formatExpiryDate,
} from '../../lib/media';
import BookingForm from '../../components/portal/MediaBookingForm';
import RescheduleEventDialog from '../../components/portal/RescheduleEventDialog';
import CancelEventDialog from '../../components/portal/CancelEventDialog';
import MediaPackageInfo from './MediaPackageInfo';

// /portal/media — school-facing Media Calendar tab.
//
// Sections:
//   1. PackageBalanceCard      — credits remaining, expires-on, status pill
//   2. Booking entry point     — "Book an event" button (gated)
//   3. BookingForm modal       — questionnaire (slides in/out)
//   4. UpcomingEvents          — pending + booked (own only)
//   5. PastEvents              — delivered + cancelled (collapsible)
//
// Tab visibility is decided by DashboardLayout (must own a package, any
// status). The booking button is the second gate — active + remaining > 0.

export default function MediaCalendar() {
  const [packages, setPackages] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'calendar'

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, e] = await Promise.all([
        api.get('/api/client/media/packages'),
        api.get('/api/client/media/events'),
      ]);
      setPackages(p?.packages || []);
      setEvents(e?.events || []);
    } catch (err) {
      setError(err?.message || 'Failed to load media calendar');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activePackage = useMemo(
    () => packages.find((p) => p.status === 'active' && p.eventsRemaining > 0)
       || packages.find((p) => p.status === 'active')
       || null,
    [packages],
  );

  // Schools that have never held a media package see the info/upsell page
  // instead of the calendar. The instant a media invoice is signed, the
  // sign-hook inserts an active media_packages row, so the next load returns a
  // non-empty packages array and the full calendar renders automatically.
  // (On a load error we fall through to the calendar shell, which surfaces the
  // error banner rather than masking it behind the marketing page.)
  if (!loading && !error && packages.length === 0) {
    return <MediaPackageInfo />;
  }

  const canBook = activePackage && activePackage.eventsRemaining > 0;
  const allReservedMessage = activePackage && activePackage.eventsRemaining === 0 && activePackage.eventsReserved > 0;
  const allExhausted = !activePackage || activePackage.status !== 'active';

  const openBooking = (prefillStart = null) => {
    setBookingPrefill(prefillStart);
    setBookingOpen(true);
  };

  const onBooked = (result) => {
    setBookingOpen(false);
    setBookingPrefill(null);
    setToast({
      type: 'success',
      message: 'Booking submitted. The NexSav team will review it and email you once it\u2019s approved and scheduled.',
    });
    setTimeout(() => setToast(null), 6000);
    load();
  };

  const upcoming = events.filter((e) => ['pending', 'booked', 'shooting', 'editing'].includes(e.status));
  const past = events.filter((e) => ['delivered', 'cancelled', 'rescheduled_out'].includes(e.status));

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Media <span className="serif-acc accent-text">Calendar</span></h1>
          <p className="text-sm text-gray-500 mt-1">
            Book event coverage from your media package and track delivery.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-white/[0.04] border border-white/[0.08] p-0.5">
            <button type="button" onClick={() => setView('list')}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] ${view === 'list' ? 'bg-[#B0FC38] text-gray-900' : 'text-gray-400 hover:text-white'}`}>
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button type="button" onClick={() => setView('calendar')}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] ${view === 'calendar' ? 'bg-[#B0FC38] text-gray-900' : 'text-gray-400 hover:text-white'}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Calendar
            </button>
          </div>
          {canBook && (
            <button
              type="button"
              onClick={() => openBooking()}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all"
            >
              <Plus className="w-4 h-4" />
              Book an event
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-[#B0FC38]/[0.08] border border-[#B0FC38]/30 text-[13px] text-gray-200">
          {toast.message}
        </div>
      )}

      {error && (
        <div className="mb-4 text-[13px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-[13px] text-gray-500">Loading…</div>
      ) : (
        <>
          <PackagesGrid packages={packages} />

          {!canBook && !loading && (
            <BookingDisabledNotice
              allReserved={allReservedMessage}
              allExhausted={allExhausted}
              reservedCount={activePackage?.eventsReserved || 0}
            />
          )}

          {view === 'calendar' ? (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 mt-2">
              <CalendarView
                events={events.map((e) => ({
                  id: e.id, title: e.eventTitle, start: e.scheduledStartAt, end: e.scheduledEndAt, status: e.status, raw: e,
                }))}
                selectable={canBook}
                onSelectSlot={canBook ? (slot) => openBooking(slot.start) : undefined}
                onSelectEvent={(ev) => setDetailTarget(ev.raw)}
              />
              {canBook && <p className="text-[11px] text-gray-500 mt-2">Tip: click an empty day to start a booking for that date.</p>}
            </div>
          ) : (
            <>
              <UpcomingSection
                events={upcoming}
                onReschedule={(ev) => setRescheduleTarget(ev)}
                onCancel={(ev) => setCancelTarget(ev)}
              />
              {past.length > 0 && <PastSection events={past} />}
            </>
          )}
        </>
      )}

      {bookingOpen && activePackage && (
        <BookingForm
          pkg={activePackage}
          initialStartAt={bookingPrefill}
          onClose={() => { setBookingOpen(false); setBookingPrefill(null); }}
          onBooked={onBooked}
        />
      )}

      {rescheduleTarget && (
        <RescheduleEventDialog
          event={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onSubmitted={() => {
            setRescheduleTarget(null);
            setToast({ type: 'success', message: 'Reschedule request submitted. NexSav will review and notify you.' });
            setTimeout(() => setToast(null), 6000);
            load();
          }}
        />
      )}

      {detailTarget && (
        <MediaEventDetail
          event={detailTarget}
          onClose={() => setDetailTarget(null)}
          actions={['pending', 'booked', 'shooting', 'editing'].includes(detailTarget.status) ? [
            { label: 'Request reschedule', onClick: () => { setRescheduleTarget(detailTarget); setDetailTarget(null); } },
            { label: 'Cancel booking', variant: 'danger', onClick: () => { setCancelTarget(detailTarget); setDetailTarget(null); } },
          ] : []}
        />
      )}

      {cancelTarget && (
        <CancelEventDialog
          event={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={(r) => {
            setCancelTarget(null);
            const msg = r.creditOutcome === 'consumed'
              ? 'Booking cancelled. Credit consumed per the under-48-hour policy.'
              : 'Booking cancelled. Credit restored to your package balance.';
            setToast({ type: 'success', message: msg });
            setTimeout(() => setToast(null), 6000);
            load();
          }}
        />
      )}
    </div>
  );
}

function PackagesGrid({ packages }) {
  if (!packages.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
      {packages.map((p) => <PackageCard key={p.id} pkg={p} />)}
    </div>
  );
}

function PackageCard({ pkg }) {
  const expiresSoon = pkg.status === 'active' && daysUntil(pkg.expiresOn) <= 30;
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#B0FC38]/15 border border-[#B0FC38]/30 flex items-center justify-center shrink-0">
            <Film className="w-4 h-4 text-[#B0FC38]" />
          </div>
          <div>
            <div className="text-[14px] text-white font-medium">{pkg.label}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Expires {formatExpiryDate(pkg.expiresOn)}
            </div>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${packageStatusBadgeClass(pkg.status)}`}>
          {packageStatusLabel(pkg.status)}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-bold text-[#B0FC38]">{pkg.eventsRemaining}</span>
        <span className="text-[12px] text-gray-500">/ {pkg.eventsTotal} events remaining</span>
      </div>
      {(pkg.eventsUsed > 0 || pkg.eventsReserved > 0) && (
        <div className="text-[11px] text-gray-500 mt-1.5">
          {pkg.eventsUsed} used · {pkg.eventsReserved} awaiting NexSav approval
        </div>
      )}
      {expiresSoon && (
        <div className="mt-3 text-[11px] text-amber-300 inline-flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Expires in {daysUntil(pkg.expiresOn)} days. Book any remaining events soon.
        </div>
      )}
    </div>
  );
}

function BookingDisabledNotice({ allReserved, allExhausted, reservedCount }) {
  let message;
  if (allReserved) {
    message = `All your remaining events are awaiting NexSav approval${reservedCount > 0 ? ` (${reservedCount} pending)` : ''}. Cancel a pending booking to free a credit, or wait for the NexSav team to review.`;
  } else if (allExhausted) {
    message = 'Your media package is fully used. Contact us to purchase a new package.';
  } else {
    message = 'Booking unavailable right now.';
  }
  return (
    <div className="mb-6 rounded-xl border border-amber-400/30 bg-amber-500/[0.04] p-4 flex items-start gap-3">
      <AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
      <div className="text-[13px] text-amber-100/90">{message}</div>
    </div>
  );
}

function UpcomingSection({ events, onReschedule, onCancel }) {
  if (!events.length) {
    return (
      <section className="mb-8">
        <h2 className="text-[13px] font-medium text-white tracking-tight mb-3">Upcoming events</h2>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-[13px] text-gray-500">
          No bookings yet. Use the button above to book your first event.
        </div>
      </section>
    );
  }
  return (
    <section className="mb-8">
      <h2 className="text-[13px] font-medium text-white tracking-tight mb-3">
        Upcoming events ({events.length})
      </h2>
      <div className="space-y-2">
        {events.map((e) => (
          <EventRow key={e.id} ev={e} onReschedule={onReschedule} onCancel={onCancel} />
        ))}
      </div>
    </section>
  );
}

function PastSection({ events }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mb-8">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-[13px] font-medium text-white tracking-tight mb-3 inline-flex items-center gap-1.5 hover:text-gray-300"
      >
        {open ? '▾' : '▸'} Past events ({events.length})
      </button>
      {open && (
        <div className="space-y-1.5">
          {events.map((e) => <EventRow key={e.id} ev={e} compact />)}
        </div>
      )}
    </section>
  );
}

function EventRow({ ev, compact = false, onReschedule = null, onCancel = null }) {
  const canSelfServe = !compact && ['pending', 'booked'].includes(ev.status);
  return (
    <div className={`rounded-xl border border-white/[0.06] bg-white/[0.02] ${compact ? 'px-3 py-2' : 'p-4'}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-white font-medium ${compact ? 'text-[13px]' : 'text-sm'}`}>{ev.eventTitle}</span>
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${eventStatusBadgeClass(ev.status)}`}>
              {eventStatusLabel(ev.status)}
            </span>
            {ev.noticeTier !== 'standard' && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${noticeTierBadgeClass(ev.noticeTier)}`}>
                {noticeTierLabel(ev.noticeTier)}
              </span>
            )}
            {ev.turnaround === 'rush' && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500/[0.1] border border-orange-400/30 text-orange-300">
                Rush turnaround
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-1">
            <CalendarIcon className="w-3 h-3" />
            {formatMediaDateRange(ev.scheduledStartAt, ev.scheduledEndAt)}
          </div>
        </div>
        {ev.status === 'delivered' && ev.driveFolderUrl && (
          // MP.7 — Drive link surfaces only when status flips to delivered
          // (gated server-side by serializer + spec §11 status-promotion
          // rule). No leak of partial-delivery URLs.
          <a href={ev.driveFolderUrl} target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-[#B0FC38] hover:text-white inline-flex items-center gap-1">
            View deliverables <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      {canSelfServe && (onReschedule || onCancel) && (
        <div className="mt-3 flex items-center gap-2 pt-3 border-t border-white/[0.04]">
          {onReschedule && (
            <button type="button" onClick={() => onReschedule(ev)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] text-gray-300 hover:text-white border border-white/[0.08] hover:border-white/[0.15]">
              <CalendarClock className="w-3 h-3" /> Request reschedule
            </button>
          )}
          {onCancel && (
            <button type="button" onClick={() => onCancel(ev)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] text-red-300 hover:text-white border border-red-400/20 hover:border-red-400/40 hover:bg-red-500/[0.06]">
              <XCircle className="w-3 h-3" /> Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (Number.isNaN(d.valueOf())) return Infinity;
  return Math.ceil((d - new Date()) / 86_400_000);
}
